import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Grid, Paper, Typography, Card, CardContent, Container, Box } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { PageHeader } from '../../common';
import { allPageData } from '../../../data/pageData';

const styles = (theme) => ({
  pageContainer: {
    backgroundColor: "#f8f9fa",
    minHeight: "100vh",
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(8),
  },
  contentContainer: {
    maxWidth: "1400px",
    margin: "0 auto",
    padding: theme.spacing(0, 4),
  },
  pageTitle: {
    fontSize: "36px",
    fontWeight: 700,
    color: "#1e3a5f",
    marginBottom: theme.spacing(4),
    fontFamily: '"Georgia", serif',
  },
  section: {
    marginBottom: theme.spacing(6),
  },
  sectionTitle: {
    fontSize: "24px",
    fontWeight: 600,
    color: "#1a1a1a",
    marginBottom: theme.spacing(3),
    fontFamily: '"Georgia", serif',
  },
  cardGrid: {
    marginTop: theme.spacing(4),
  },
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 10px 20px rgba(0, 0, 0, 0.1)",
    },
    border: "1px solid #e0e0e0",
  },
  cardClickable: {
    cursor: "pointer",
  },
  cardNonClickable: {
    cursor: "default",
    opacity: 0.85,
    background: "#f5f5f5",
    border: "1px solid #e0e0e0",
  },
  cardContent: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  cardTitle: {
    fontSize: "18px",
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: "#1e3a5f",
    fontFamily: '"Georgia", serif',
  },
  breadcrumb: {
    marginBottom: theme.spacing(4),
    color: "#666",
    fontSize: "14px",
  },
  breadcrumbLink: {
    color: "#1e3a5f",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
    marginRight: "8px",
  },
  content: {
    fontSize: "16px",
    lineHeight: 1.8,
    color: "#444",
    fontFamily: '"Segoe UI", sans-serif',
    "& p": {
      marginBottom: theme.spacing(2),
    },
  },
});

// Now using centralized page data from src/data/pageData.js

function NestedPage(props) {
  const { classes, pageId, customData } = props;
  const location = useLocation();
  const navigate = useNavigate();
  // Get path segments for breadcrumb navigation
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const pathKey = pathSegments.length > 0 ? pathSegments[0] : 'default';
  
  // Get page key from either props or URL path
  const getPageKey = () => {
    // If pageId is provided in props, use that
    if (pageId) return pageId;
    
    // Otherwise derive from URL path
    const pathName = location.pathname;
    const pathSegments = pathName.split('/').filter(Boolean);
    return pathSegments.length > 0 ? pathSegments[0] : 'default';
  };
  
  // Get page content from either props or data store
  const getPageContent = () => {
    // If custom data is provided via props, use that
    if (customData) return customData;
    
    // Otherwise look up data from the store
    const pageKey = getPageKey();
    
    // Find the data for this page, or use default if not found
    for (const key in allPageData) {
      if (location.pathname.includes(key)) {
        return allPageData[key];
      }
    }
    
    // If no match is found, return default
    return allPageData['default'];
  };
  
  const pageContent = getPageContent();
  
  // Check if this page is a sub-service with a parent service
  const hasParentService = pageContent && 'parentService' in pageContent;
  const parentServicePath = hasParentService ? `/${pageContent.parentService}` : null;
  const parentServiceName = hasParentService && allPageData[pageContent.parentService] ? 
    allPageData[pageContent.parentService].title : '';
  
  return (
    <div className={classes.pageContainer}>
      <Container maxWidth="lg" className={classes.contentContainer}>
        <div className={classes.breadcrumb}>
          <a href="/" className={classes.breadcrumbLink}>Home</a> &gt; 
          {hasParentService && (
            <>
              <a href={parentServicePath} className={classes.breadcrumbLink} onClick={(e) => {
                e.preventDefault();
                navigate(parentServicePath);
                window.scrollTo(0, 0);
              }}>{parentServiceName}</a> &gt; 
            </>
          )}
          {pageContent.title}
        </div>
        
        <Typography variant="h1" className={classes.pageTitle}>
          {pageContent.title}
        </Typography>
        
        <div className={classes.section}>
          <div className={classes.content}>
            <p>{pageContent.content}</p>
            <p>We understand that each case is unique and requires a tailored approach. Our team works closely with you to understand your specific needs and develop strategies that align with your goals. With years of experience and a deep understanding of the legal landscape, we are equipped to handle even the most complex legal challenges.</p>
            <p>Our commitment to excellence and client satisfaction drives everything we do. We leverage cutting-edge technology and innovative methodologies to deliver efficient and effective legal solutions.</p>
          </div>
        </div>
        
        <div className={classes.section}>
          <Typography variant="h2" className={classes.sectionTitle}>
            Related Services
          </Typography>
          
          <Grid container spacing={4} className={classes.cardGrid}>
            {pageContent.relatedServices.map((service, index) => (
              <Grid item md={3} sm={6} xs={12} key={index}>
                <Card 
                  className={`${classes.card} ${(!service.path || location.pathname.includes(service.path)) ? classes.cardNonClickable : classes.cardClickable}`}
                  onClick={() => {
                    // Only navigate if the path exists and is different from current path
                    if (service.path && !location.pathname.includes(service.path)) {
                      navigate(service.path);
                      window.scrollTo(0, 0);
                    }
                  }}
                  className={`${classes.card} ${(!service.path || location.pathname.includes(service.path)) ? classes.cardNonClickable : classes.cardClickable}`}
                >
                  <CardContent className={classes.cardContent}>
                    <Typography className={classes.cardTitle}>
                      {service.title}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </div>
      </Container>
    </div>
  );
}

export default withStyles(styles)(NestedPage);